import React, { useEffect, useState } from "react";
import API_BASE from "../../api";
import bgLeaf from "../assets/HomePage/contact-f.jpg";

export default function ContactFormRight() {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    subject: "",
    message: ""
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const elements = document.querySelectorAll(".contact-animate");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fadeUpSoft");
            entry.target.classList.remove("opacity-0");
          }
        });
      },
      { threshold: 0.25 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => elements.forEach((el) => observer.unobserve(el));
  }, []);

  const isValidName = (name) => {
    if (!/^[A-Za-z]{2,}(?: [A-Za-z]{2,})+$/.test(name)) return false;
    if (/^(a+|asd+|asdf+|qwerty+|test.)$/i.test(name)) return false;
    return true;
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, "");
    return cleaned.length >= 9 && cleaned.length <= 15;
  };

  const isSpamMessage = (text) => {
    const patterns = [
      /http/i,
      /https/i,
      /viagra/i,
      /porn/i,
      /free money/i
    ];
    return patterns.some((p) => p.test(text));
  };

  const validateForm = () => {
    const newErr = {};

    if (!isValidName(form.full_name))
      newErr.full_name = "Enter a proper full name (2+ words, only letters).";

    if (!isValidPhone(form.phone))
      newErr.phone = "Enter a valid international phone number.";

    if (!isValidEmail(form.email))
      newErr.email = "Enter a valid email address.";

    if (form.subject.trim().length < 3)
      newErr.subject = "Subject must contain at least 3 characters.";

    if (form.message.trim().length < 10)
      newErr.message = "Message must contain at least 10 characters.";

    if (isSpamMessage(form.message))
      newErr.message = "Your message contains spam-like content.";

    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setSuccess("");

    try {
      const res = await fetch(`${API_BASE}/contact_manager/add_message.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });

      const data = await res.json();

      if (data.success) {
        setSuccess("Message sent successfully. We will contact you shortly.");
        setForm({
          full_name: "",
          phone: "",
          email: "",
          subject: "",
          message: ""
        });
        setErrors({});
      } else {
        setSuccess("Failed to send message. Try again later.");
      }
    } catch (err) {
      setSuccess("Network error. Try again later.");
    }

    setLoading(false);
  };

  return (
    <section
      className="relative w-full py-20 bg-no-repeat bg-center bg-cover bg-fixed"
      style={{ backgroundImage: `url(${bgLeaf})` }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"></div>

      <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 items-center px-6 gap-10">

        <div className="bg-white/90 backdrop-blur-md rounded-xl p-8 md:p-10 shadow-lg">
          <h2 className="text-3xl md:text-4xl font-serif text-[#3A2F2A] mb-8 contact-animate opacity-0">
            Contact Us
          </h2>

          <form className="space-y-5" onSubmit={handleSubmit}>

            {/* Full Name */}
            <input
              type="text"
              placeholder="Full Name"
              className="w-full p-4 rounded-lg border border-gray-300 focus:border-[#6D4C30] outline-none contact-animate opacity-0"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            />
            {errors.full_name && <p className="text-red-600 text-sm">{errors.full_name}</p>}

            {/* Phone */}
            <input
              type="tel"
              placeholder="Mobile Number"
              className="w-full p-4 rounded-lg border border-gray-300 focus:border-[#6D4C30] outline-none contact-animate opacity-0"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            {errors.phone && <p className="text-red-600 text-sm">{errors.phone}</p>}

            {/* Email */}
            <input
              type="email"
              placeholder="Email Address"
              className="w-full p-4 rounded-lg border border-gray-300 focus:border-[#6D4C30] outline-none contact-animate opacity-0"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            {errors.email && <p className="text-red-600 text-sm">{errors.email}</p>}

            {/* Subject */}
            <input
              type="text"
              placeholder="Subject"
              className="w-full p-4 rounded-lg border border-gray-300 focus:border-[#6D4C30] outline-none contact-animate opacity-0"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />
            {errors.subject && <p className="text-red-600 text-sm">{errors.subject}</p>}

            {/* Message */}
            <textarea
              rows="4"
              placeholder="Message"
              className="w-full p-4 rounded-lg border border-gray-300 focus:border-[#6D4C30] outline-none contact-animate opacity-0"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            ></textarea>
            {errors.message && <p className="text-red-600 text-sm">{errors.message}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full p-4 bg-[#6D4C30] text-white font-semibold rounded-lg hover:bg-[#3A2F2A] transition-all duration-300 contact-animate opacity-0"
            >
              {loading ? "Sending..." : "Send Message"}
            </button>

            {success && <p className="text-green-600 mt-3">{success}</p>}
          </form>
        </div>

        <div className="h-[280px] md:h-[420px]"></div>
      </div>
    </section>
  );
}
